import React from 'react';
import { Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import {
  FileText, Building, Package, Users, AlertTriangle, Zap, Clock,
  RefreshCw, Sparkles, Shield, HardDrive, Bot, History, BookOpen,
  Receipt, ClipboardList, FolderOpen, Timer, LayoutDashboard, Wallet, Landmark,
  ArrowLeftRight, Plug, ScanEye, Tags, GitCompare, Inbox as InboxIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

// Feature definitions for display in the subscription section
const FEATURE_DEFINITIONS = {
  // Core — included in all plans
  bookkeeping: { name: 'Bokföring', Icon: BookOpen, description: 'Grundläggande bokföring och huvudbok', category: 'core' },
  invoicing: { name: 'Fakturering', Icon: FileText, description: 'Skapa och skicka kundfakturor', category: 'core' },
  invoicing_quotes: { name: 'Offerter', Icon: ClipboardList, description: 'Skapa offerter och konvertera till fakturor', category: 'core' },
  supplier_invoices: { name: 'Leverantörsfakturor', Icon: Receipt, description: 'Hantera inköpsfakturor', category: 'core' },
  payroll: { name: 'Lönehantering', Icon: Wallet, description: 'Löneberäkning och utbetalning', category: 'core' },
  personell_management: { name: 'Personalhantering', Icon: Users, description: 'Hantera anställda och avdelningar', category: 'core' },
  dashboard_widget: { name: 'Dashboard', Icon: LayoutDashboard, description: 'Översiktspanel och rapporter', category: 'core' },
  // Professional — AI & integrations
  banking_integration: { name: 'Bankintegration', Icon: Landmark, description: 'Automatisk import av banktransaktioner', category: 'premium' },
  ai_ocr: { name: 'AI OCR', Icon: ScanEye, description: 'Automatisk fakturatolkning', category: 'ai' },
  ai_categorization: { name: 'AI-kategorisering', Icon: Tags, description: 'Smart kontokodning', category: 'ai' },
  // Enterprise — full AI & advanced features
  ai_reconciliation: { name: 'AI-avstämning', Icon: GitCompare, description: 'Automatisk bankavstämning', category: 'ai' },
  ai_assistant: { name: 'AI-assistent', Icon: Bot, description: 'Intelligent bokföringshjälp', category: 'ai' },
  multi_currency: { name: 'Flera valutor', Icon: ArrowLeftRight, description: 'Bokföring i flera valutor', category: 'premium' },
  document_management: { name: 'Dokumenthantering', Icon: FolderOpen, description: 'Avancerad dokumentlagring', category: 'premium' },
  time_tracking: { name: 'Tidrapportering', Icon: Timer, description: 'Tidsredovisning och projekt', category: 'premium' },
  api_access: { name: 'API-åtkomst', Icon: Plug, description: 'Programmatisk åtkomst via REST API', category: 'premium' },
  email_inbox_scanning: { name: 'E-postinkorg', Icon: InboxIcon, description: 'Automatisk hämtning av fakturor från Gmail/Outlook', category: 'premium' }
};

const getChangeTypeLabel = (changeType) => {
  const labels = {
    created: 'Skapad',
    plan_upgrade: 'Plan uppgraderad',
    plan_downgrade: 'Plan nedgraderad',
    cancelled: 'Avslutad',
    reactivated: 'Återaktiverad',
    trial_started: 'Provperiod startad',
    trial_ended: 'Provperiod avslutad',
    credits_purchased: 'Krediter köpta',
    credits_reset: 'Krediter återställda',
    addon_purchased: 'Tillägg köpt',
    addon_cancelled: 'Tillägg avslutat'
  };
  return labels[changeType] || changeType;
};

const formatSubscriptionDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'd MMMM yyyy', { locale: sv });
  } catch {
    return dateStr;
  }
};

// Subscription & features section — plan overview, resource usage, available
// add-ons and plans, and subscription history. Subscription data is lazy-loaded
// in the CompanySettings shell and passed in as props; modals (credit purchase,
// plan upgrade) live in the shell and are opened via the setShow* props.
const SubscriptionSection = ({
  entitlements,
  caps,
  stripeConfigured,
  cancelLoading,
  resourceUsage,
  plans,
  availableAddons,
  features,
  addonCheckoutLoading,
  subscriptionHistory,
  handleCancelSubscription,
  refreshEntitlements,
  fetchSubscriptionData,
  handleAddonCheckout,
  setShowCreditModal,
  setShowPlanModal,
}) => {
  const creditPercentage = entitlements?.aiCredits
    ? Math.round((entitlements.aiCredits.balance / entitlements.aiCredits.monthlyAllocation) * 100)
    : 0;

  return (
    <>
      {/* Plan Overview */}
      <div className="modern-card" id="oversikt">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <Shield size={24} />
              Prenumerationsöversikt
            </h2>
            <p className="section-subtitle">
              {entitlements?.plan?.displayName || 'Ingen plan'}
              <Badge
                bg={entitlements?.status === 'active' ? 'success' : entitlements?.status === 'trial' ? 'info' : 'warning'}
                className="ms-2"
              >
                {entitlements?.status === 'active' ? 'Aktiv' :
                  entitlements?.status === 'trial' ? 'Provperiod' :
                    entitlements?.status === 'cancelled' ? 'Avslutad' : entitlements?.status || 'Okänd'}
              </Badge>
              <span className="ms-3">
                {formatSubscriptionDate(entitlements?.periodStart)} – {formatSubscriptionDate(entitlements?.periodEnd)}
              </span>
            </p>
          </div>
          <div className="action-buttons d-flex gap-2">
            {/* Owner-only per §2.4. caps.isOwner false hides the button
                entirely; backend still 403s the request via
                requireOwnerRole as the actual gate. */}
            {caps.isOwner && stripeConfigured && entitlements?.plan?.name && entitlements.plan.name !== 'beta_free' && entitlements?.status === 'active' && (
              <Button
                variant="outline-danger"
                size="sm"
                className="d-flex align-items-center gap-1"
                disabled={cancelLoading}
                onClick={handleCancelSubscription}
              >
                {cancelLoading ? 'Avslutar...' : 'Avsluta plan'}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { refreshEntitlements(); fetchSubscriptionData(); }}
            >
              <RefreshCw size={14} />
              Uppdatera
            </Button>
          </div>
        </div>
        <div className="modern-card-body">
          {(() => {
            const usage = resourceUsage?.usage;
            const limits = entitlements?.limits;
            const metrics = [
              { label: 'Användare', Icon: Users, current: usage?.usersCount ?? limits?.currentUsers ?? 0, limit: limits?.maxUsers },
              { label: 'Kunder', Icon: Building, current: usage?.customers ?? '–', limit: limits?.maxCustomers },
              { label: 'Kundfakturor', Icon: FileText, current: usage?.invoicesThisMonth ?? '–', limit: limits?.maxInvoicesPerMonth, suffix: '/mån' },
              { label: 'Lev.fakturor', Icon: Receipt, current: usage?.supplierInvoicesThisMonth ?? '–', limit: limits?.maxSupplierInvoicesPerMonth, suffix: '/mån' },
              { label: 'Verifikationer', Icon: BookOpen, current: usage?.verificationsThisMonth ?? '–', limit: limits?.maxVerificationsPerMonth, suffix: '/mån' },
              { label: 'Anställda', Icon: Users, current: usage?.employees ?? '–', limit: limits?.maxEmployees },
              { label: 'Lagring', Icon: HardDrive, current: usage?.storageBytes ? (usage.storageBytes / (1024 * 1024 * 1024)).toFixed(2) : '0', limit: limits?.maxStorageGb, unit: 'GB' },
            ];

            const aiCredits = entitlements?.aiCredits?.monthlyAllocation > 0 ? entitlements.aiCredits : null;

            return (
              <div className="feature-cards">
                {metrics.map((m, idx) => {
                  const hasLimit = m.limit !== null && m.limit !== undefined;
                  return (
                    <div className="feature-card" key={idx}>
                      <div className="feature-icon core">
                        <m.Icon size={18} />
                      </div>
                      <div className="feature-content">
                        <h4 className="feature-name">{m.label}</h4>
                        <div className="feature-benefits">
                          <span className="benefit-tag">
                            {m.unit
                              ? `${m.current} / ${hasLimit ? m.limit : '∞'} ${m.unit}`
                              : `${m.current} / ${hasLimit ? m.limit : '∞'}`}
                            {m.suffix ? ` ${m.suffix}` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {aiCredits && (
                  <div className="feature-card">
                    <div className="feature-icon core">
                      <Bot size={18} />
                    </div>
                    <div className="feature-content">
                      <div className="feature-header">
                        <h4 className="feature-name">AI-krediter</h4>
                        {stripeConfigured && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}
                            onClick={() => setShowCreditModal(true)}
                          >
                            <Zap size={10} className="me-1" />
                            Köp krediter
                          </Button>
                        )}
                      </div>
                      <p className="feature-description" style={{ marginBottom: '0.25rem' }}>
                        {aiCredits.fromPlan > 0 && <span>Från plan: {aiCredits.fromPlan}</span>}
                        {aiCredits.fromPlan > 0 && aiCredits.purchased > 0 && <span> · </span>}
                        {aiCredits.purchased > 0 && <span>Köpta: {aiCredits.purchased}</span>}
                      </p>
                      <div className="feature-benefits">
                        <span className="benefit-tag">
                          {aiCredits.balance} / {aiCredits.monthlyAllocation} kvar
                        </span>
                        {creditPercentage <= 25 && (
                          <span className="benefit-tag" style={{
                            background: creditPercentage <= 10 ? '#fef2f2' : '#fffbeb',
                            color: creditPercentage <= 10 ? '#991b1b' : '#92400e',
                            borderColor: creditPercentage <= 10 ? '#fca5a5' : '#fbbf24'
                          }}>
                            <AlertTriangle size={10} className="me-1" />
                            {creditPercentage <= 10 ? 'Nästan slut' : 'Lågt saldo'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Add-on Features — only show add-ons not already included in the plan */}
      {(() => {
        const currentPlan = plans.find(p => p.plan_name === entitlements?.plan?.name);
        const basePlanFeatures = currentPlan?.features || {};
        const extraAddons = availableAddons.filter(addon => !basePlanFeatures[addon.feature_key]);
        if (extraAddons.length === 0) return null;
        return (
          <div className="modern-card" id="tillagg">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Package size={24} />
                  Tilläggsfunktioner
                </h2>
                <p className="section-subtitle">
                  Utöka din plan med extra funktioner
                </p>
              </div>
            </div>
            <div className="modern-card-body">
              <div className="feature-cards">
                {extraAddons.map((addon) => {
                  const def = FEATURE_DEFINITIONS[addon.feature_key];
                  const isEnabled = !!features[addon.feature_key];

                  return (
                    <div className={`feature-card ${isEnabled ? '' : 'premium'}`} key={addon.addon_id}>
                      <div className={`feature-icon ${isEnabled ? 'core' : 'premium'}`}>
                        {def ? <def.Icon size={18} /> : <Package size={18} />}
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">{addon.display_name}</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id={`addon-${addon.feature_key}`}
                              checked={isEnabled}
                              disabled
                              readOnly
                            />
                            <label htmlFor={`addon-${addon.feature_key}`} className={`toggle-label ${isEnabled ? '' : 'premium'}`} style={{ opacity: 0.6, cursor: 'not-allowed' }}></label>
                          </div>
                        </div>
                        <p className="feature-description">{addon.description || def?.description}</p>
                        <div className="feature-benefits">
                          {isEnabled
                            ? <span className="benefit-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>✓ Aktiverad</span>
                            : stripeConfigured ? (
                              <>
                                <span className="benefit-tag" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fbbf24' }}>
                                  {addon.price_monthly_sek} kr/mån
                                </span>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                                  disabled={addonCheckoutLoading === addon.addon_id}
                                  onClick={() => handleAddonCheckout(addon.addon_id)}
                                >
                                  {addonCheckoutLoading === addon.addon_id ? 'Laddar...' : 'Köp'}
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="benefit-tag" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fbbf24', filter: 'blur(4px)', userSelect: 'none' }}>
                                  {addon.price_monthly_sek} kr/mån
                                </span>
                                <span className="benefit-tag" style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0' }}>
                                  Kommer snart
                                </span>
                              </>
                            )
                          }
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Available Plans */}
      <div className="modern-card" id="planer">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <Sparkles size={24} />
              Tillgängliga planer
            </h2>
            <p className="section-subtitle">
              Jämför planer och välj den som passar ditt företag (Dessa är temporära och kan ändras innan officiell lansering)
            </p>
          </div>
        </div>
        <div className="modern-card-body">
          {!stripeConfigured && (
            <Alert variant="info" className="mb-4">
              <Sparkles size={16} className="me-2" />
              Under betaperioden är alla konton på <strong>Beta (Gratis)</strong>-planen.
              Uppgradering kommer att vara tillgänglig efter beta.
            </Alert>
          )}
          {stripeConfigured && entitlements?.status === 'past_due' && (
            <Alert variant="warning" className="mb-4">
              <AlertTriangle size={16} className="me-2" />
              <strong>Din betaperiod har löpt ut.</strong> Välj en plan för att fortsätta använda alla funktioner.
            </Alert>
          )}

          <Row>
            {plans.map((plan) => {
              const isCurrentPlan = plan.plan_name === entitlements?.plan?.name;
              const planFeatures = typeof plan.features === 'string'
                ? JSON.parse(plan.features)
                : plan.features || {};

              return (
                <Col lg={3} md={6} key={plan.plan_id} className="mb-4">
                  <div style={{
                    background: 'white',
                    border: `2px solid ${isCurrentPlan ? 'var(--bs-primary)' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    padding: '1.5rem',
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {isCurrentPlan && (
                      <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)' }}>
                        <Badge bg="primary" className="px-3 py-1">Din plan</Badge>
                      </div>
                    )}
                    <h5 className="text-center mb-3" style={{ fontWeight: 600 }}>{plan.display_name}</h5>

                    <div className="text-center mb-3">
                      <h3 className="mb-0" style={plan.price_monthly_sek > 0 ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>
                        {plan.price_monthly_sek > 0
                          ? `${plan.price_monthly_sek} kr`
                          : 'Gratis'}
                      </h3>
                      {plan.price_monthly_sek > 0 && (
                        <small className="text-muted" style={{ filter: 'blur(5px)', userSelect: 'none' }}>/månad</small>
                      )}
                    </div>

                    <hr />

                    <ul className="list-unstyled" style={{ flex: 1 }}>
                      <li className="mb-2">
                        <Users size={14} className="me-2 text-primary" />
                        <strong>{plan.max_users}</strong> användare
                      </li>
                      <li className="mb-2">
                        <Building size={14} className="me-2 text-primary" />
                        <strong>{plan.max_customers ?? '∞'}</strong> kunder
                      </li>
                      <li className="mb-2">
                        <FileText size={14} className="me-2 text-primary" />
                        <strong>{plan.max_invoices_per_month ?? '∞'}</strong> fakturor/mån
                      </li>
                      <li className="mb-2">
                        <Receipt size={14} className="me-2 text-primary" />
                        <strong>{plan.max_supplier_invoices_per_month ?? '∞'}</strong> lev.fakturor/mån
                      </li>
                      <li className="mb-2">
                        <BookOpen size={14} className="me-2 text-primary" />
                        <strong>{plan.max_verifications_per_month ?? '∞'}</strong> verifikationer/mån
                      </li>
                      <li className="mb-2">
                        <Users size={14} className="me-2 text-primary" />
                        <strong>{plan.max_employees ?? '∞'}</strong> anställda
                      </li>
                      <li className="mb-2">
                        <HardDrive size={14} className="me-2 text-primary" />
                        <strong>{plan.max_storage_gb} GB</strong> lagring
                      </li>
                      {plan.monthly_ai_credits > 0 && (
                        <li className="mb-2">
                          <Bot size={14} className="me-2 text-primary" />
                          <strong>{plan.monthly_ai_credits}</strong> AI-krediter/mån
                        </li>
                      )}
                      {(() => {
                        const premiumFeatures = Object.entries(planFeatures)
                          .filter(([key, v]) => v === true && FEATURE_DEFINITIONS[key]?.category !== 'core');
                        if (premiumFeatures.length === 0) return null;
                        return (
                          <>
                            <li className="mb-2 mt-3">
                              <small className="text-muted">Inkluderar även:</small>
                            </li>
                            {premiumFeatures.map(([key]) => {
                              const def = FEATURE_DEFINITIONS[key] || { name: key };
                              return (
                                <li key={key} className="mb-1 ms-3" style={{ listStyleType: 'disc' }}>
                                  <small>{def.name}</small>
                                </li>
                              );
                            })}
                          </>
                        );
                      })()}
                      {plan.plan_name === 'beta_free' && (
                        <li className="mb-1 mt-3 ms-3" style={{ listStyleType: 'disc' }}>
                          <small className="text-muted fst-italic">Fler funktioner tillkommer under beta</small>
                        </li>
                      )}
                    </ul>

                    <div className="d-grid mt-3">
                      {isCurrentPlan ? (
                        <Button variant="outline-primary" disabled size="sm">
                          Nuvarande plan
                        </Button>
                      ) : plan.price_monthly_sek <= 0 ? (
                        <Button variant="outline-secondary" disabled size="sm">
                          Gratis
                        </Button>
                      ) : stripeConfigured ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowPlanModal(true)}
                        >
                          Välj plan
                        </Button>
                      ) : (
                        <Button variant="primary" disabled size="sm">
                          Kommer snart
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>

      {/* Subscription History */}
      <div className="modern-card" id="historik">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <History size={24} />
              Historik
            </h2>
            <p className="section-subtitle">
              Ändringar i din prenumeration
            </p>
          </div>
        </div>
        <div className="modern-card-body">
          {subscriptionHistory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <History size={40} />
              </div>
              <h4>Ingen prenumerationshistorik ännu</h4>
              <p>Ändringar i din prenumeration kommer att visas här</p>
            </div>
          ) : (
            <div>
              {subscriptionHistory.map((entry, index) => (
                <div key={index} className="d-flex mb-3 pb-3" style={{ borderBottom: index < subscriptionHistory.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div className="me-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', background: 'var(--rgba-primary-1)' }}>
                      <Clock size={16} className="text-primary" />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong style={{ fontSize: '0.9rem' }}>{getChangeTypeLabel(entry.change_type)}</strong>
                        {entry.details && (
                          <p className="text-muted small mb-0 mt-1">
                            {typeof entry.details === 'string'
                              ? entry.details
                              : JSON.stringify(entry.details)}
                          </p>
                        )}
                      </div>
                      <small className="text-muted">
                        {formatSubscriptionDate(entry.changed_at)}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionSection;
